/**
 * Карточка чата мессенджера `/messenger/chat/<id>`: сделки по телефону,
 * привязки CRM и лента сообщений с картинками. Спека
 * `docs/specs/2026-09-25-messenger-chats-design.md`; те же данные —
 * `prokat-runner messenger-chat ID`.
 */
import { useQuery } from "@tanstack/react-query";
import { ChevronLeft } from "lucide-react";
import { useState } from "react";
import { Link, useParams } from "react-router";

import { LeadLink } from "@/components/LeadLink";
import { type ChatCard, type ChatItem, DIR_LABEL, dt, loadChat } from "@/lib/messenger";

const card = "rounded-[var(--radius-card)] border border-border bg-bg-subtle";

export function Bubble({ i }: { i: ChatItem }) {
  const out = i.dir === "out" || i.dir === "sender";
  const meta = (
    <div className="mb-0.5 text-[11px] text-fg-faint">
      {dt(i.at)}
      {i.kind === "msg" ? ` · ${i.dir ? DIR_LABEL[i.dir] : ""}${i.author ? ` · ${i.author}` : ""}` : " · файл"}
      {i.template ? ` · шаблон «${i.template}»` : ""}
    </div>
  );
  return (
    <div className={`flex ${out ? "justify-end" : "justify-start"}`}>
      <div
        className={`max-w-[75%] rounded-[var(--radius-ui)] border border-border px-3 py-2 text-[13px] ${out ? "bg-bg-hover" : "bg-bg"}`}
      >
        {meta}
        {i.kind === "media" ? (
          i.url ? (
            i.image ? (
              <a href={i.url} target="_blank" rel="noreferrer">
                <img src={i.url} alt="картинка из переписки" loading="lazy" className="max-h-64 rounded" />
              </a>
            ) : (
              <a href={i.url} target="_blank" rel="noreferrer" className="underline">
                файл
              </a>
            )
          ) : (
            <span className="text-fg-faint">файл без пути</span>
          )
        ) : (
          <div className="whitespace-pre-wrap break-words">{i.text ?? <span className="text-fg-faint">—</span>}</div>
        )}
      </div>
    </div>
  );
}

function Header({ c }: { c: ChatCard }) {
  const h = c.chat;
  return (
    <section className={`${card} p-4 text-[12.5px]`} data-annot="шапка чата">
      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
        <span className="text-[15px] font-semibold">{h.who ?? h.messenger_id ?? `#${h.id}`}</span>
        <span className="text-fg-muted">{h.system}</span>
        <span className="text-fg-muted">{h.accounts.join(", ")}</span>
        <span className={h.status === "Не отвечено" ? "text-err" : "text-fg-muted"}>{h.status}</span>
        {h.group ? <span className="text-fg-faint">группа · копий {h.ids.length}</span> : null}
        {h.first_at ? (
          <span className="text-fg-faint">
            первое сообщение {h.first_at.slice(8, 10)}.{h.first_at.slice(5, 7)}.{h.first_at.slice(0, 4)}
          </span>
        ) : null}
      </div>
      {c.phones.length ? <div className="mt-2 text-fg-muted">Телефоны: {c.phones.join(", ")}</div> : null}
      {!h.group ? (
        <div className="mt-3 grid gap-3 md:grid-cols-2">
          <div>
            <div className="mb-1 text-[11.5px] text-fg-faint">Сделки по телефону</div>
            {c.deals.length ? (
              c.deals.map((d) => (
                <div key={d.id}>
                  <Link to={`/deals/${d.id}`} className="hover:underline">
                    {d.id}
                  </Link>
                  <span className="ml-2 text-fg-muted">{d.stage}</span>
                  <span className="ml-2 text-fg-faint">{d.at?.slice(0, 10)}</span>
                </div>
              ))
            ) : (
              <span className="text-fg-faint">не нашлось</span>
            )}
          </div>
          <div>
            <div className="mb-1 text-[11.5px] text-fg-faint">Привязки CRM</div>
            {c.binds.length ? (
              c.binds.map((b, n) => (
                <div key={n} className="text-fg-muted">
                  {b.deal_id ? (
                    <Link to={`/deals/${b.deal_id}`} className="text-fg hover:underline">
                      сделка {b.deal_id}
                    </Link>
                  ) : null}
                  {b.lead_id ? <span className="ml-2">лид <LeadLink id={b.lead_id} /></span> : null}
                  <span className="ml-2 text-fg-faint">{b.source}</span>
                </div>
              ))
            ) : (
              <span className="text-fg-faint">нет</span>
            )}
          </div>
        </div>
      ) : null}
    </section>
  );
}

export function MessengerChatPage() {
  const id = Number(useParams().chatId);
  const first = useQuery({ queryKey: ["chat", id], queryFn: () => loadChat(id), enabled: id > 0 });
  // Страницы «раньше» — старые впереди; первая страница — последние сообщения.
  const [older, setOlder] = useState<ChatCard[]>([]);
  const [loading, setLoading] = useState(false);
  const tail = older[0] ?? first.data;

  const more = async () => {
    if (!tail?.before) return;
    setLoading(true);
    try {
      const page = await loadChat(id, tail.before);
      setOlder((prev) => [page, ...prev]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto p-5">
      <Link
        to="/messenger?tab=chats"
        className="flex w-fit items-center gap-1 text-[12.5px] text-fg-muted hover:text-fg"
      >
        <ChevronLeft size={14} /> Мессенджер · чаты
      </Link>
      {first.isPending ? (
        <div className="text-[12.5px] text-fg-muted">Читаю чат из CRM…</div>
      ) : first.isError ? (
        <div className="text-[12.5px] text-err">Не удалось получить чат: {String(first.error.message)}</div>
      ) : (
        <>
          <Header c={first.data} />
          <section className={`${card} flex flex-col gap-2 p-4`} data-annot="лента чата">
            {tail?.more ? (
              <button
                type="button"
                onClick={more}
                disabled={loading}
                className="mx-auto rounded-[var(--radius-ui)] border border-border px-3 py-1 text-[12.5px] text-fg-muted hover:bg-bg-hover"
              >
                {loading ? "…" : "Раньше"}
              </button>
            ) : null}
            {[...older, first.data].flatMap((p) => p.items).map((i) => (
              <Bubble key={`${i.kind}-${i.id}`} i={i} />
            ))}
            {!first.data.items.length ? <div className="text-[12.5px] text-fg-faint">Сообщений нет.</div> : null}
          </section>
        </>
      )}
    </main>
  );
}
